import { Module } from 'vuex'

import { GetCourseUseCase } from '@/application/problem/getCourseUseCase'
import { Course } from '@/domain/entities/problem/course'
import { ProblemRepository } from '@/domain/repositories/problem/ProblemRepository'
import { DisplayLanguageType } from '@/domain/value_objects/language/displayLanguageType'

interface CourseState {
  courseList: { [id: number]: Course }
  isLoading: boolean
  error: string | null
}

export const courseModule = (problemRepository: ProblemRepository): Module<CourseState, any> => {
  const getCourseUseCase = new GetCourseUseCase(problemRepository)

  return {
    namespaced: true,
    state: {
      courseList: {},
      isLoading: false,
      error: null
    },
    mutations: {
      setCourseList(state, { courseList }: { courseList: Course[] }) {
        const newList = courseList.reduce(
          (acc, course) => ({ ...acc, [course.id]: course }),
          {} as { [id: number]: Course }
        )
        state.courseList = { ...state.courseList, ...newList }
      },
      setCourseById(state, { course }: { course: Course }) {
        state.courseList = { ...state.courseList, [course.id]: course }
      },
      setIsLoading(state, isLoading: boolean) {
        state.isLoading = isLoading
      },
      setError(state, error: string | null) {
        state.error = error
      }
    },
    actions: {
      async getCourseList({ commit }, { displayLanguage }: { displayLanguage: DisplayLanguageType }) {
        commit('setIsLoading', true)
        commit('setError', null)
        try {
          const res = await getCourseUseCase.getCourseList(displayLanguage)
          commit('setCourseList', { courseList: res })
        } catch (e) {
          commit('setError', e)
        } finally {
          commit('setIsLoading', false)
        }
      },
      async getCourseById(
        { commit },
        { displayLanguage, courseId }: { displayLanguage: DisplayLanguageType; courseId: number }
      ) {
        commit('setIsLoading', true)
        commit('setError', null)
        try {
          const res = await getCourseUseCase.getCourseById(displayLanguage, courseId)
          commit('setCourseById', { course: res })
        } catch (e) {
          commit('setError', e)
        } finally {
          commit('setIsLoading', false)
        }
      }
    },
    getters: {
      courseList: state => state.courseList,
      isLoading: state => state.isLoading,
      error: state => state.error
    }
  }
}
